import { UM } from '../_components/tokens';
import type { Layer, Step, StepId } from './types';

// ─── Primitives visuelles ────────────────────────────────────────────────────

function EyeToggle({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  const c = visible ? UM.accent : '#b0c4d4';
  return (
    <span
      onClick={onClick}
      title={visible ? 'Masquer la couche' : 'Afficher la couche'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, borderRadius: '50%',
        background: visible ? UM.accentLight : '#f1f5f9',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      <svg width="13" height="9" viewBox="0 0 13 9">
        <ellipse cx="6.5" cy="4.5" rx="6" ry="4" stroke={c} strokeWidth="1.3" fill="none" />
        <circle cx="6.5" cy="4.5" r="2" fill={c} />
      </svg>
    </span>
  );
}

function SourceBadge({ source }: { source: 'engie' | 'open' }) {
  const isEngie = source === 'engie';
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
      background: isEngie ? UM.badgeBlue : '#f1f5f9',
      color: isEngie ? UM.badgeBlueText : UM.textMuted,
      padding: '2px 6px', borderRadius: 3,
      textTransform: 'uppercase', flexShrink: 0,
    }}>
      {isEngie ? 'ENGIE' : 'Open Data'}
    </span>
  );
}

function TrashIcon({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title="Retirer de mon panel"
      style={{
        border: 'none', background: 'none', cursor: 'pointer',
        padding: 0, width: 22, height: 22, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        color: UM.textMuted,
      }}
    >
      <svg width="12" height="13" viewBox="0 0 12 13">
        <path d="M2 3h8M4.5 3V2a1 1 0 011-1h1a1 1 0 011 1v1M3 3l.5 8a1 1 0 001 1h3a1 1 0 001-1L9 3" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" />
      </svg>
    </button>
  );
}

// ─── Ligne de couche ─────────────────────────────────────────────────────────

interface LayerRowProps {
  layer: Layer;
  visible: boolean;
  onToggleVisible: () => void;
  onRemove: () => void;
}

function LayerRow({ layer, visible, onToggleVisible, onRemove }: LayerRowProps) {
  return (
    <div
      title={layer.hint}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = UM.bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <EyeToggle visible={visible} onClick={onToggleVisible} />
      <span style={{
        flex: 1, fontSize: 13,
        color: visible ? UM.text : UM.textMuted,
        fontWeight: visible ? 500 : 400,
      }}>
        {layer.label}
      </span>
      <SourceBadge source={layer.source} />
      <TrashIcon onClick={onRemove} />
    </div>
  );
}

// ─── Picker : bibliothèque complète (overlay) ────────────────────────────────

interface PickerProps {
  steps: Step[];
  library: Set<string>;
  results: Layer[];
  search: string;
  stepFilter: StepId | null;
  catalogSize: number;
  librarySize: number;
  onSearch: (s: string) => void;
  onSetStepFilter: (s: StepId | null) => void;
  onToggle: (id: string) => void;
  onClose: () => void;
}

function StepChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 12,
        border: `1px solid ${active ? UM.accent : UM.border}`,
        background: active ? UM.accent : UM.bg,
        color: active ? '#fff' : UM.textMuted,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        lineHeight: 1.2,
      }}
    >
      {label}
    </button>
  );
}

function PickerRow({ layer, selected, onToggle }: { layer: Layer; selected: boolean; onToggle: () => void }) {
  return (
    <div
      onClick={onToggle}
      title={selected ? 'Déjà dans votre panel — cliquez pour retirer' : 'Cliquez pour ajouter à votre panel'}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 16px', cursor: 'pointer',
        background: 'transparent',
        opacity: selected ? 0.55 : 1,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = UM.bgHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        width: 22, height: 22, borderRadius: '50%',
        background: selected ? '#e5eef4' : UM.accentLight,
        color: selected ? UM.textMuted : UM.accent,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {selected ? (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path d="M1 4.5 4 7.5 10 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1 }}>+</span>
        )}
      </span>
      <span style={{
        flex: 1, fontSize: 13, color: UM.text,
        fontWeight: 400,
      }}>
        {layer.label}
      </span>
      {selected && (
        <span style={{ fontSize: 10, fontWeight: 600, color: UM.textMuted, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          Ajoutée
        </span>
      )}
      <SourceBadge source={layer.source} />
    </div>
  );
}

function LibraryPicker({ steps, library, results, search, stepFilter, catalogSize, librarySize, onSearch, onSetStepFilter, onToggle, onClose }: PickerProps) {
  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: UM.bg, display: 'flex', flexDirection: 'column',
      zIndex: 20,
    }}>
      <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${UM.border}`, background: UM.bgHeader }}>
        <button
          onClick={onClose}
          style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.accent, fontSize: 12, padding: 0, marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          ← Retour à mon panel
        </button>

        <div style={{ fontWeight: 700, fontSize: 15, color: UM.text, marginBottom: 10 }}>
          Bibliothèque de couches
        </div>

        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Rechercher une couche…"
            style={{
              width: '100%', padding: '7px 10px 7px 30px', borderRadius: 6,
              border: `1px solid ${UM.border}`, fontSize: 13, background: UM.bg, color: UM.text,
              boxSizing: 'border-box',
            }}
          />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: UM.textDim, fontSize: 13 }}>⌕</span>
        </div>

        <div style={{ fontSize: 11, color: UM.textMuted, marginTop: 6 }}>
          {stepFilter || search.trim()
            ? `${results.length} couche${results.length > 1 ? 's' : ''} sur ${catalogSize}`
            : `${catalogSize} couches disponibles — ${librarySize} dans votre panel`}
        </div>
      </div>

      {/* Chips de filtre par étape */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 6,
        padding: '10px 16px', borderBottom: `1px solid ${UM.border}`, background: UM.bg,
      }}>
        {steps.map((s) => (
          <StepChip
            key={s.id}
            label={s.short}
            active={stepFilter === s.id}
            onClick={() => onSetStepFilter(stepFilter === s.id ? null : s.id)}
          />
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
        {results.length === 0 ? (
          <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 13, color: UM.textDim }}>
            Aucune couche ne correspond à votre recherche.
          </div>
        ) : (
          results.map((layer) => (
            <PickerRow
              key={layer.id}
              layer={layer}
              selected={library.has(layer.id)}
              onToggle={() => onToggle(layer.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export interface LibraryPanelProps {
  steps: Step[];
  library: Set<string>;
  libraryLayers: Layer[];
  visible: Set<string>;
  pickerOpen: boolean;
  pickerSearch: string;
  pickerStep: StepId | null;
  pickerResults: Layer[];
  activeCount: number;
  librarySize: number;
  catalogSize: number;
  onSetPickerOpen: (open: boolean) => void;
  onSetPickerSearch: (s: string) => void;
  onSetPickerStep: (s: StepId | null) => void;
  onToggleVisible: (id: string) => void;
  onToggleLibrary: (id: string) => void;
  onRemoveFromLibrary: (id: string) => void;
  onClearAll: () => void;
  onClose: () => void;
}

export function LibraryPanel(props: LibraryPanelProps) {
  return (
    <div style={{
      width: 340, minWidth: 340, background: UM.bg,
      borderRight: `1px solid ${UM.border}`,
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden', zIndex: 15,
      position: 'relative',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${UM.border}`, background: UM.bgHeader, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: UM.text }}>Couches de données</span>
          <button
            onClick={props.onClose}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.textMuted, fontSize: 18, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>
        <div style={{ fontSize: 11, color: UM.textDim, lineHeight: 1.4, marginBottom: 8 }}>
          Composez librement votre panel avec les couches qui comptent pour vous.
        </div>
        <button
          onClick={() => props.onSetPickerOpen(true)}
          style={{
            padding: 0,
            border: 'none',
            background: 'none',
            color: UM.accent,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
          Ajouter des couches
        </button>
      </div>

      {/* Couches favorites */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {props.libraryLayers.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 13, color: UM.textDim }}>
            Votre panel est vide.<br />
            <span style={{ fontSize: 12 }}>Cliquez sur "+" pour choisir vos couches.</span>
          </div>
        ) : (
          props.libraryLayers.map((layer) => (
            <LayerRow
              key={layer.id}
              layer={layer}
              visible={props.visible.has(layer.id)}
              onToggleVisible={() => props.onToggleVisible(layer.id)}
              onRemove={() => props.onRemoveFromLibrary(layer.id)}
            />
          ))
        )}
      </div>

      {/* Footer — état des couches actives */}
      <div style={{
        padding: '10px 16px',
        borderTop: `1px solid ${UM.border}`,
        background: UM.bgHeader,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 12,
      }}>
        <span style={{ color: UM.textMuted }}>
          {props.activeCount === 0 ? 'Aucune couche activée' : (
            <><strong style={{ color: UM.accent }}>{props.activeCount}</strong> couche{props.activeCount > 1 ? 's' : ''} activée{props.activeCount > 1 ? 's' : ''}</>
          )}
        </span>
        {props.activeCount > 0 && (
          <button
            onClick={props.onClearAll}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: UM.textMuted, fontSize: 12, textDecoration: 'underline' }}
          >
            Tout masquer
          </button>
        )}
      </div>

      {props.pickerOpen && (
        <LibraryPicker
          steps={props.steps}
          library={props.library}
          results={props.pickerResults}
          search={props.pickerSearch}
          stepFilter={props.pickerStep}
          catalogSize={props.catalogSize}
          librarySize={props.librarySize}
          onSearch={props.onSetPickerSearch}
          onSetStepFilter={props.onSetPickerStep}
          onToggle={props.onToggleLibrary}
          onClose={() => { props.onSetPickerOpen(false); props.onSetPickerSearch(''); props.onSetPickerStep(null); }}
        />
      )}
    </div>
  );
}
